import * as React from 'react'
import styled from 'styled-components'
import {theme} from '../theme'
import {Button} from '../elements/Button'
import {Separator} from '../elements/Separator'
import {QuestionStore} from '../stores/QuestionStore'
import {AnswerStore} from '../stores/AnswerStore'
import {observer, inject} from 'mobx-react'
import {answerOptions} from '../types/answer'
import {Stores} from '../stores'
import {RouteComponentProps} from 'react-router'
import {computed} from 'mobx'
import CandidateInfo from '../components/CandidateInfo'

interface IProps extends RouteComponentProps<{candidateId: string}> {
  questionStore: QuestionStore
  answerStore: AnswerStore
}
@inject((stores: Stores) => ({
  answerStore: stores.answerStore,
  questionStore: stores.questionStore
}))
@observer
export class AnswersPage extends React.Component<IProps> {
  @computed
  get candidateId() {
    return this.props.match.params.candidateId
  }

  private getAnswer = (questionId: number): string | undefined => {
    const answer = this.props.answerStore.answers.find(a => a.question_id === questionId)
    return answer && answer.answer ? answerOptions.find(o => o.id === answer.answer)!.text : undefined
  }

  private editAnswer = (questionId: number) => {
    this.props.answerStore.startEditing()
    this.props.history.push(`/ehdokas/${this.candidateId}/kysymys/${questionId}`)
  }

  public render() {
    return (
      <>
        <Content>
          <Disclaimer>Ole hyvä ja tarkista vastauksesi ennen niiden tallentamista</Disclaimer>
          <Container>
            <CandidateInfo />
            <span>Yhteenveto vastauksista</span>
            {this.props.questionStore.questions.map((question, index) => {
              const text = this.getAnswer(question.id)
              const {explanation = undefined} = this.props.answerStore.answers.find(a => a.question_id === question.id) || {}
              return (
                <AnswerWrapper key={question.id}>
                  <Separator />
                  <Question>
                    {index + 1}. {question.text}
                  </Question>
                  <AnswerContainer>
                    <div>
                      {text ? <Span color={'#609125'}>Vastattu:</Span> : <Span color={theme.color.red}>Ohitettu:</Span>}
                      <Span width={'150px'}>{text || 'Ei vastausta'}</Span>
                    </div>
                    <EditAnswer color={theme.color.red} onClick={() => this.editAnswer(question.id)}>
                      Muokkaa vastausta
                    </EditAnswer>
                  </AnswerContainer>
                  {explanation && <Explanation>{`Perustelut: ${explanation}`}</Explanation>}
                </AnswerWrapper>
              )
            })}
          </Container>
          <PrintContainer>
            <Separator />
            <Print>
              Voit halutessasi ladata vastaukset <RedSpan onClick={() => window.print()}>PDF-muodossa.</RedSpan>
            </Print>
          </PrintContainer>
          <ActionButtons>
            <Button
              color={'white'}
              height="40px"
              fontSize="18px"
              onClick={() => this.props.history.push(`/ehdokas/${this.candidateId}/kysymys/${this.props.questionStore.questions[0].id}`)}
            >
              Muokkaa
            </Button>
            <Button height="40px" fontSize="18px" onClick={() => this.props.history.push(`/ehdokas/${this.candidateId}/kiitos`)}>
              Tallenna
            </Button>
          </ActionButtons>
        </Content>
      </>
    )
  }
}

const Span = styled.div<{color?: string; width?: string}>`
  display: inline-block;
  color: ${props => props.color || 'auto'};
  width: ${props => props.width || '100px'};
`

const EditAnswer = styled.span`
  cursor: pointer;
  color: ${theme.color.red};
  @media screen and (max-width: ${theme.breakPoints.mobileMax}px) {
    margin-left: 100px;
  }
`

const Disclaimer = styled.div`
  font-weight: 500;
  color: #222;
  font-size: 2.2rem;
  padding-bottom: 4rem;
  @media screen and (max-width: ${theme.breakPoints.mobileLandscape}px) {
    font-size: 2rem;
    padding-bottom: 2rem;
  }
  @media screen and (max-width: ${theme.breakPoints.mobileMax}px) {
    font-size: 1.8rem;
  }
`

const ActionButtons = styled.div`
  display: flex;
  justify-content: space-between;
  flex-wrap: wrap;
  ${Button} {
    margin-bottom: 2px;
  }
`

const Container = styled.div``

const AnswerWrapper = styled.div`
  padding: 0.4rem 0;
  ${Separator} {
    margin-top: 0 !important;
  }
`

const AnswerContainer = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  padding-bottom: 1.4rem;
  font-size: 1.7rem;
  @media screen and (max-width: ${theme.breakPoints.mobileLandscape}px) {
    padding-bottom: 0.7rem;
    span {
      font-size: 1.6rem;
    }
  }
`

const Question = styled.div`
  font-size: 1.7rem;
  padding: 1.7rem 0;
  font-weight: 600;
  @media screen and (max-width: ${theme.breakPoints.mobileLandscape}px) {
    padding: 0.9rem 0;
  }
  @media screen and (max-width: ${theme.breakPoints.mobileLandscape}px) {
    font-size: 1.6rem;
  }
`

const PrintContainer = styled.div`
  padding-top: 4px;
  ${Separator} {
    margin-top: 0 !important;
  }
`

const Print = styled.div`
  margin-bottom: 3rem;
  padding-top: 17px;
  font-size: 17px;
  cursor: pointer;
`

const RedSpan = styled.span`
  color: ${theme.color.red};
  text-decoration: underline;
`

const Content = styled.div`
  font-weight: 500;
  padding: 4rem 12.5rem 3rem 12.8rem;
  @media screen and (max-width: ${theme.breakPoints.mobileLandscape}px) {
    padding: 2rem 4rem;
  }
  @media screen and (max-width: ${theme.breakPoints.mobileMax}px) {
    padding: ${theme.layout.mobileContentPadding};
  }
  @media print {
    ${Disclaimer} {
      display: none;
    }
    ${EditAnswer} {
      display: none;
    }
    ${ActionButtons} {
      display: none;
    }
    ${Print} {
      display: none;
    }
  }
`

const Explanation = styled.div`
  margin-bottom: 8px;
  white-space: pre-line;
  overflow-wrap: break-word;
`
